import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  CreditCard, Calendar, Check, Zap, Download, ChevronRight,
  Shield, Users, BarChart3, Sparkles, ArrowUpRight, Loader, Hash, Mail
} from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useSubscriptionStatusContext } from '../hooks/SubscriptionStatusContext';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../services/api';

const SubscriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  padding-bottom: ${props => props.theme.spacing[8]};
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
`;

const PlanCard = styled(Card)<{ $featured?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  position: relative;
  overflow: hidden;

  ${props => props.$featured && `
    border-color: ${props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props.theme.colors.primary}20;
  `}
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing[4]};
  right: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
`;

const PlanHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const PlanIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: ${props => props.theme.borders.radius.lg};
  background: ${props => props.$color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
`;

const PlanName = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const PlanDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[3]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const DetailValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const StatusBadge = styled.span<{ $status: 'active' | 'inactive' }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  background: ${props => props.$status === 'active' ? props.theme.colors.success : props.theme.colors.error}20;
  color: ${props => props.$status === 'active' ? props.theme.colors.success : props.theme.colors.error};
  border: 1px solid ${props => props.$status === 'active' ? props.theme.colors.success : props.theme.colors.error}40;
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  background: ${props => props.$variant === 'primary' ? props.theme.colors.primary : props.theme.colors.backgroundTertiary};
  color: ${props => props.$variant === 'primary' ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.$variant === 'primary' ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[3]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border-radius: ${props => props.theme.borders.radius.md};
`;

const FeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.success};
  flex-shrink: 0;
`;

const FeatureText = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.5;
`;

const DetailsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DetailsTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[8]} 0;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface SubscriptionDetails {
  hasSubscription: boolean;
  id?: string;
  plano: string;
  status?: string;
  ativo: boolean;
  createdAt?: string;
  expiresAt?: string;
  userEmail?: string;
  userName?: string;
  daysUntilExpiration?: number;
}

const Subscription: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { status: subscriptionStatus } = useSubscriptionStatusContext();

  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        console.log('[Subscription] 🔍 Buscando detalhes para:', user.uid);
        const details = await subscriptionApi.getDetails(user.uid);
        console.log('[Subscription] ✅ Detalhes recebidos:', details);
        setSubscriptionDetails(details);
      } catch (error) {
        console.error('[Subscription] ❌ Erro ao buscar detalhes:', error);
        setSubscriptionDetails({
          hasSubscription: false,
          plano: 'Free',
          ativo: false
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [user?.uid]);

  const isActive = subscriptionDetails?.ativo ?? false;
  const planName = subscriptionDetails?.plano || 'Free';

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const planFeatures = {
    Free: [
      'Acesso básico ao CRM',
      'Até 100 leads',
      'Relatórios mensais',
      'Suporte via email'
    ],
    Premium: [
      'Acesso completo ao CRM',
      'Leads ilimitados',
      'Relatórios em tempo real',
      'Automações avançadas',
      'Integração com múltiplas plataformas',
      'Suporte prioritário 24/7',
      'Backup automático diário',
      'API personalizada'
    ]
  };

  if (loading) {
    return (
      <SubscriptionContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <LoadingSpinner size={48} style={{ color: theme.colors.primary }} />
        </div>
      </SubscriptionContainer>
    );
  }

  return (
    <SubscriptionContainer>
      <PageHeader>
        <PageTitle>Gerenciar Assinatura</PageTitle>
        <PageSubtitle>Acompanhe seu plano e gerencie pagamentos</PageSubtitle>
      </PageHeader>

      <ContentGrid>
        {/* Card do Plano Atual */}
        <PlanCard $featured={isActive}>
          {isActive && (
            <FeaturedBadge>
              <Sparkles size={12} />
              Plano Ativo
            </FeaturedBadge>
          )}

          <PlanHeader>
            <PlanIcon $color={isActive ? theme.colors.primary : theme.colors.textTertiary}>
              <Zap size={28} />
            </PlanIcon>
            <div>
              <PlanName>Plano {planName}</PlanName>
              <PlanDescription>
                {isActive
                  ? 'Você está aproveitando todos os recursos premium do Loopra.'
                  : 'Você está no plano gratuito. Faça upgrade para desbloquear recursos premium.'}
              </PlanDescription>
            </div>
          </PlanHeader>

          <PlanDetails>
            <DetailRow>
              <DetailLabel>
                <Shield size={16} />
                Status
              </DetailLabel>
              <StatusBadge $status={isActive ? 'active' : 'inactive'}>
                {isActive ? 'Ativo' : 'Inativo'}
              </StatusBadge>
            </DetailRow>
          </PlanDetails>

          <Button
            $variant="primary"
            onClick={() => navigate('/upgrade')}
          >
            {isActive ? 'Alterar Plano' : 'Fazer Upgrade'}
            <ArrowUpRight size={16} />
          </Button>
        </PlanCard>

        {/* Card de Recursos */}
        <DetailsCard>
          <DetailsHeader>
            <DetailsTitle>Recursos do Plano</DetailsTitle>
            <BarChart3 size={20} style={{ color: theme.colors.primary }} />
          </DetailsHeader>

          <FeaturesList>
            {(planFeatures[planName as keyof typeof planFeatures] || planFeatures.Free).map((feature, index) => (
              <FeatureItem key={index}>
                <FeatureIcon>
                  <Check size={20} />
                </FeatureIcon>
                <FeatureText>{feature}</FeatureText>
              </FeatureItem>
            ))}
          </FeaturesList>
        </DetailsCard>
      </ContentGrid>

      {/* Card de Detalhes da Assinatura */}
      {subscriptionDetails?.hasSubscription && (
        <DetailsCard>
          <DetailsHeader>
            <DetailsTitle>Detalhes da Assinatura</DetailsTitle>
            <CreditCard size={20} style={{ color: theme.colors.primary }} />
          </DetailsHeader>
          <PlanDetails>
            <DetailRow>
              <DetailLabel>
                <Hash size={16} />
                ID da Assinatura
              </DetailLabel>
              <DetailValue>{subscriptionDetails.id || 'N/A'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                <Mail size={16} />
                Email de Cobrança
              </DetailLabel>
              <DetailValue>{subscriptionDetails.userEmail || 'N/A'}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                <Calendar size={16} />
                Data da Assinatura
              </DetailLabel>
              <DetailValue>{formatDate(subscriptionDetails.createdAt)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>
                <Calendar size={16} />
                Próxima Renovação
              </DetailLabel>
              <DetailValue>
                {formatDate(subscriptionDetails.expiresAt)}
                {subscriptionDetails?.daysUntilExpiration !== null &&
                  subscriptionDetails?.daysUntilExpiration !== undefined && (
                    <span style={{ marginLeft: theme.spacing[2], color: theme.colors.textTertiary, fontSize: theme.typography.fontSize.xs }}>
                      ({subscriptionDetails.daysUntilExpiration} dias)
                    </span>
                  )}
              </DetailValue>
            </DetailRow>
          </PlanDetails>
        </DetailsCard>
      )}

      {/* Card de Faturas */}
      <DetailsCard>
        <DetailsHeader>
          <DetailsTitle>Histórico de Faturas</DetailsTitle>
          <Download size={20} style={{ color: theme.colors.primary }} />
        </DetailsHeader>

        <EmptyState>
          <CreditCard size={48} style={{ color: theme.colors.textTertiary }} />
          <EmptyStateText>
            {isActive
              ? 'Suas faturas aparecerão aqui quando forem geradas.'
              : 'Faça upgrade para acessar o histórico de faturas.'}
          </EmptyStateText>
          {!isActive && (
            <Button $variant="primary" onClick={() => navigate('/upgrade')}>
              Ver Planos
              <ChevronRight size={16} />
            </Button>
          )}
        </EmptyState>
      </DetailsCard>
    </SubscriptionContainer>
  );
};

export default Subscription;